/**
 * The shell root.
 *
 * `h-dvh` rather than `min-h-dvh`: the shell is exactly the viewport, and the
 * content column inside it owns the scroll. With `min-h-dvh` the root grew to
 * fit its content, so a page containing a fixed-height panel with an inner
 * scroll region pushed the root taller instead of scrolling inside the panel —
 * the panel's own footer drifted off-screen and the whole page scrolled.
 *
 * `overflow-hidden` keeps the fixed sidebar and bottom nav from being scrolled
 * away by a stray overflow.
 */
export const shellClasses = 'flex h-dvh overflow-hidden bg-surface';

/**
 * The column beside the sidebar.
 *
 * `min-h-0` is load-bearing, not cosmetic. A flex child defaults to
 * `min-height: auto`, which means it refuses to shrink below its content — so a
 * page containing a fixed-height panel with an inner scroll region would grow
 * this column instead of letting the panel scroll, and the whole page would
 * scroll while the panel's own footer drifted off-screen. `min-h-0` lets the
 * column shrink to the viewport so the innermost scroll region wins.
 */
export const shellContentClasses = 'flex min-h-0 min-w-0 flex-1 flex-col';

/**
 * The content frame, and the ONE owner of page padding.
 *
 * Page padding lives here rather than in each page layout because the shell is
 * the only thing every route passes through. When each layout also carried its
 * own `p-6`, a page inherited `px-6` from the shell AND another `p-6` from its
 * layout, and a card nested inside the state wrapper added a third — 73px of
 * dead gutter per side, which at 620px is nearly a quarter of the viewport.
 * Owning it once means a new page cannot forget it and cannot double it.
 *
 * The canvas is `surface-sunken`, one step below the chrome and two below a
 * card. `surface` and `surface-raised` are only ~1% apart, so a white card on
 * a near-white canvas showed almost no elevation and read as a mis-coloured
 * hole rather than a raised surface. Recessing the canvas gives cards real
 * separation without touching a single token value.
 *
 * Page gutter is capped at `1rem` in every direction — the canvas reads
 * tighter and keeps more of the viewport for content. The bottom gutter is
 * `pb-4` like the rest; the extra clearance a bottom nav needs is added on
 * top of it by `shellMainWithBottomNavClasses`, never instead of it.
 *
 * `overflow-y-auto` makes this the ONE scroll container for ordinary pages.
 * A page that wants its own inner scroll (a stepper with a pinned footer)
 * simply does not overflow, so this never engages for it.
 */
export const shellMainClasses =
  'flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-sunken px-4 pt-4 pb-4';

/**
 * The extra bottom-nav clearance, applied ONLY when the nav is rendered.
 *
 * The bottom nav is `lg:hidden`, so the clearance has to survive `md` and is
 * released at `lg`. But the nav is also driven by the navigation registry: a
 * deployment whose registry flags no destination `mobile` renders an EMPTY
 * nav, and reserving 96px to clear a bar that is not there leaves a band of
 * dead canvas under every page — the table card ended 95px above the bottom
 * of the viewport with nothing in between.
 *
 * So the clearance is a separate class the shell adds only when the nav has
 * items. It is `pb-24` (96px) rather than `pb-20` because it must clear the
 * nav's own height AND leave the ordinary `pb-4` gutter visible above it —
 * the nav is ~64px tall, so 96px of padding leaves ~16px of canvas between
 * the card and the nav, matching the gutter on every other side.
 */
export const shellMainWithBottomNavClasses = 'pb-24 lg:pb-4';
